/**
 * Политика за поверителност — the text as returned by the legal team.
 *
 * Reproduced rather than paraphrased. It is a legal document: the wording
 * is the deliverable, and tightening a sentence would change what the
 * site has undertaken to do. Where the draft left a placeholder it is
 * rendered as a visible marker rather than dropped or filled with a
 * guess — an unfinished privacy notice that looks finished is the worst
 * of the three outcomes.
 *
 * Two placeholders are wired to real controls instead, because they
 * describe something this page can actually do: the reader's current
 * consent, and the button that changes it.
 *
 * The draft's substance is checked against what the code does, not just
 * accepted: ЕГН are never stored (etl/transformers/id_hasher.py), source
 * and update date are kept per node (`sources`, `first_seen`,
 * `last_seen`), a masked identifier is reported rather than guessed at
 * (etl/sources/company_info.py), person URLs are reduced before
 * reporting (lib/analytics.ts), and the tag does not load before
 * consent. Those sentences are claims the pipeline has to keep true.
 */

import { PageHead, SectionHead } from '../components/ui';
import {
  analyticsConfigured, clearConsent, storedConsent,
} from '../lib/analytics';

/** A blank legal has not filled in yet. Deliberately loud. */
function Todo({ children }: Readonly<{ children: string }>) {
  return <mark className="todo-slot">[{children}]</mark>;
}

function ConsentState() {
  if (!analyticsConfigured()) {
    return (
      <p>
        Текущ избор: <strong>тази инсталация не зарежда Google Analytics</strong>{' '}
        и не записва статистически бисквитки.
      </p>
    );
  }
  const current = storedConsent();
  const LABELS = { granted: 'съгласие', denied: 'отказ' } as const;
  const label = current ? LABELS[current] : 'все още не сте питани';
  return (
    <p>
      Текущ избор: <strong>{label}</strong>
      {current && (
        <>
          {' · '}
          <button
            type="button"
            className="linklike"
            onClick={() => { clearConsent(); window.location.reload(); }}
          >
            промяна на избора
          </button>
        </>
      )}
    </p>
  );
}

export function PrivacyPage() {
  return (
    <div className="prose-page">
      <PageHead
        eyebrow="Правна информация"
        title="Политика за поверителност"
        lede={'Този сайт обработва информация от публично достъпни регистри с цел '
          + 'журналистическо и обществено-информационно проследяване на разходването '
          + 'на публични средства, връзките между получатели на публични средства, '
          + 'търговски дружества, изборни длъжности и други публично значими '
          + 'обстоятелства. Тук е описано какви лични данни се обработват, за какви '
          + 'цели, на какво основание и какви са вашите права.'}
      />

      <section className="block">
        <SectionHead title="Обхват" />
        <p>
          {'Обработването се извършва при зачитане на неприкосновеността на личния '
           + 'живот и само доколкото е необходимо за свободата на изразяване и правото '
           + 'на информация. Личните данни не се използват за търговско профилиране, '
           + 'кредитна оценка, директен маркетинг или автоматизирано вземане на '
           + 'решения спрямо физически лица.'}
        </p>
      </section>

      <section className="block">
        <SectionHead title="Администратор и контакт" />
        <p>
          Администратор на личните данни е: <Todo>име/наименование</Todo>,{' '}
          <Todo>ЕИК/идентификационни данни</Todo>, със седалище и адрес на управление:{' '}
          <Todo>адрес</Todo>.
        </p>
        <p>
          {'За въпроси, искания за проверка, корекция, възражения или сигнали за '
           + 'грешки можете да се свържете на: '}
          <Todo>електронен адрес и/или пощенски адрес</Todo>.
        </p>
        <p>
          Ако е определено длъжностно лице по защита на данните, контактът с него е:{' '}
          <Todo>координати за контакт</Todo>.
        </p>
      </section>

      <section className="block">
        <SectionHead title="Какви данни се обработват" />
        <p>Обработват се само данни, необходими за целта на сайта:</p>
        <ul className="prose-list">
          <li>
            имена на собственици, управители, членове на управителни и контролни
            органи и действителни собственици на търговски дружества;
          </li>
          <li>
            имена на избрани кметове, общински съветници и кандидати, както и
            публично обявената политическа формация, която ги е издигнала;
          </li>
          <li>
            публични идентификатори на юридически лица, договори, плащания,
            програми, обществени поръчки и други данни, свързани с разходването
            на публични средства;
          </li>
          <li>
            технически данни за посещенията на сайта само при дадено съгласие за
            статистически бисквитки.
          </li>
        </ul>
        <p>
          {'Данните се обработват в контекста на професионалната, търговската, '
           + 'публичната или изборната роля на лицата. Сайтът не цели събиране или '
           + 'публикуване на данни за личния и семейния живот извън необходимото за '
           + 'обществено-информационната цел.'}
        </p>
      </section>

      <section className="block">
        <SectionHead title="ЕГН, ЛНЧ и псевдонимизация" />
        <p>
          {'ЕГН и ЛНЧ не се съхраняват в базата данни на сайта. Когато '
           + 'първоизточникът предоставя технически отпечатък вместо самия '
           + 'идентификатор, сайтът съхранява единствено този отпечатък.'}
        </p>
        <p>
          {'Това не означава, че всички данни са анонимни. Когато името, публичната '
           + 'длъжност, ролята в дружество или други обстоятелства позволяват '
           + 'идентифициране на физическо лице, информацията се третира като лични '
           + 'данни. Псевдонимизираните данни остават лични данни по смисъла на ОРЗД.'}
        </p>
        <p>
          {'Когато първоизточникът е скрил идентификатор или не позволява сигурно '
           + 'разграничаване между лица с еднакви имена, сайтът не възстановява '
           + 'връзката по догадка. Такива случаи се отбелязват като непълни или '
           + 'несигурни, вместо да се прави вероятностно съединяване.'}
        </p>
      </section>

      <section className="block">
        <SectionHead title="Източници" aside="само публично достъпни" />
        <p>
          {'Данните се събират само от публично достъпни източници, включително ЦАИС '
           + 'ЕОП и TED, ИСУН, ДФ „Земеделие“, Търговския регистър, регистър БУЛСТАТ, '
           + 'ЦИК, публично достъпни данни на ГРАО, публичния списък на длъжниците на '
           + 'НАП и публични данни за плащания през СЕБРА.'}
        </p>
        <p>
          {'Не се купуват, не се получават и не се зареждат данни от непублични '
           + 'източници. Плащания към физически лица по земеделски мерки не се '
           + 'зареждат, освен ако конкретни данни вече не са публично достъпни и са '
           + 'необходими за обществено-информационната цел.'}
        </p>
        <p>
          {'При всяко зареждане се съхранява информация за източника и датата на '
           + 'последната актуализация, за да може да се проследи произходът на данните '
           + 'и евентуална грешка в първоизточника.'}
        </p>
      </section>

      <section className="block">
        <SectionHead title="Основание за обработването" />
        <p>
          {'Обстоятелството, че дадени данни са публично достъпни, не ги изключва '
           + 'автоматично от режима за защита на личните данни.'}
        </p>
        <p>
          {'Основната цел на сайта е журналистическа и обществено-информационна: да '
           + 'улесни проверката как се разходват публични средства и какви връзки '
           + 'съществуват между публични плащания, дружества и лица с публична или '
           + 'стопанска роля. За това обработване се прилага чл. 25з от Закона за '
           + 'защита на личните данни — обработване за журналистически цели, когато е '
           + 'необходимо за свободата на изразяване и правото на информация и при '
           + 'зачитане на неприкосновеността на личния живот.'}
        </p>
        <p>
          {'Доколкото конкретна операция по обработване не е необходима за '
           + 'журналистическата цел, тя се извършва само ако има самостоятелно '
           + 'приложимо основание по ОРЗД.'}
        </p>
        <p>
          {'Данни, които могат да разкриват политическа принадлежност, се публикуват '
           + 'само в контекста на изборната роля на лицето и само доколкото това е '
           + 'необходимо за разбиране на публичната функция, изборния резултат или '
           + 'връзката с разходването на публични средства.'}
        </p>
        <p>
          {'Данните за търговски дружества — ЕИК, наименование, седалище, договори и '
           + 'плащания — по правило не са лични данни. Те могат да представляват лични '
           + 'данни, когато позволяват идентифициране на физическо лице, например при '
           + 'едноличен търговец или когато наименованието съдържа име на физическо лице.'}
        </p>
      </section>

      <section className="block">
        <SectionHead title="Вашите права" />
        <p>Можете да поискате:</p>
        <ul className="prose-list">
          <li>информация дали сайтът обработва данни за вас;</li>
          <li>достъп до конкретните записи, в които присъствате;</li>
          <li>коригиране на фактическа грешка;</li>
          <li>отбелязване на спорен или непълен запис;</li>
          <li>
            ограничаване на визуализацията, когато има риск от неправилно
            идентифициране;
          </li>
          <li>
            изтриване или скриване на данни, когато те не са необходими за целта
            на сайта или накърняват непропорционално права и законни интереси;
          </li>
          <li>възражение срещу конкретна обработка.</li>
        </ul>
        <p>
          {'Искането следва да съдържа връзка към конкретната страница или запис, '
           + 'описание на проблема, данни за контакт и, когато е приложимо, '
           + 'доказателства за грешката.'}
        </p>
        <p>
          {'Когато обработването е за журналистически цели, част от правата могат да '
           + 'бъдат ограничени или отказани, ако уважаването им би възпрепятствало '
           + 'свободата на изразяване или правото на информация. Отказът не е '
           + 'автоматичен — всеки случай се преценява според конкретните данни, ролята '
           + 'на лицето, обществения интерес и риска за личната неприкосновеност.'}
        </p>
        <p>
          {'Сигнали за сгрешено съвпадение на име, ЕИК, длъжност или участие в '
           + 'дружество се разглеждат приоритетно. Ако грешката произтича от '
           + 'първоизточника, сайтът може да отбележи несъответствието, но поправката '
           + 'в официалния регистър се извършва от компетентната институция.'}
        </p>
        <p>
          {'Ако считате, че обработването на личните ви данни нарушава приложимите '
           + 'правила, можете да подадете жалба до Комисията за защита на личните '
           + 'данни: гр. София, бул. „Проф. Цветан Лазаров“ № 2, както и чрез '
           + 'официално посочените от КЗЛД канали за контакт.'}
        </p>
      </section>

      <section className="block">
        <SectionHead title="Бисквитки и статистика" />
        <p>
          {'Сайтът използва статистически бисквитки чрез Google Analytics само след '
           + 'изрично съгласие. Скриптът не се зарежда и статистическа бисквитка не се '
           + 'записва, преди да бъде дадено съгласие. Отказът се запомня, за да не '
           + 'бъдете питани повторно при всяко посещение.'}
        </p>
        <p>
          {'При статистическо отчитане адресите на страници за физически лица се '
           + 'съкращават така, че към Google Analytics да не се изпраща псевдонимът '
           + 'или друг идентификатор на конкретното лице. Вместо това се отчита само '
           + 'обобщена категория, например „страница за лице“.'}
        </p>
        <p>
          {'Идентификаторите на юридически лица и общини могат да се използват за '
           + 'статистически цели, доколкото не разкриват поведението на конкретен '
           + 'посетител спрямо конкретно физическо лице. Можете по всяко време да '
           + 'промените избора си за статистически бисквитки от бутона по-долу.'}
        </p>
        <ConsentState />
        <p>
          {'Ако чрез Google Analytics се предават данни извън Европейския съюз или '
           + 'Европейското икономическо пространство, на тази страница се посочват '
           + 'получателят, държавата, правното основание и приложимите гаранции за '
           + 'предаването: '}
          <Todo>получател, държава, основание и гаранции</Todo>.
        </p>
      </section>

      <section className="block">
        <SectionHead title="Съхранение" />
        <p>
          {'Данните се съхраняват, докато са необходими за целта на сайта — '
           + 'проследяване на публични разходи във времето, включително исторически '
           + 'сравнения и проверка на последователност между публични регистри.'}
        </p>
        <p>
          {'Периодично се прави преглед дали определени категории данни продължават да '
           + 'са необходими. Данни, които вече не служат на обществено-информационната '
           + 'цел или създават непропорционален риск от неправилно идентифициране, '
           + 'могат да бъдат скрити, ограничени или отбелязани като несигурни.'}
        </p>
        <p>
          {'Записи, които са част от исторически контекст за публично финансиране, '
           + 'договори или изборна публичност, могат да бъдат запазени, когато '
           + 'изтриването им би нарушило целостта на анализа.'}
        </p>
      </section>

      <section className="block">
        <SectionHead title="Предоставяне на трети лица" />
        <p>
          {'Данните не се продават и не се предоставят на трети лица за самостоятелни '
           + 'търговски цели. Част от данните са публично визуализирани на сайта, '
           + 'доколкото това е необходимо за журналистическата и '
           + 'обществено-информационната цел.'}
        </p>
        <p>
          {'Технически статистически данни могат да се предават към Google Analytics '
           + 'само след съгласие за статистически бисквитки и при описаните по-горе '
           + 'ограничения. Достъп до техническата инфраструктура могат да имат '
           + 'доставчици на хостинг, поддръжка или сигурност, ако това е необходимо за '
           + 'функционирането на сайта и при задължение за поверителност и защита на '
           + 'данните.'}
        </p>
        <p>
          {'Когато външен доставчик обработва данни от името на администратора, '
           + 'отношенията се уреждат с договор или друг правен акт, който определя '
           + 'предмета, срока и целта на обработването, видовете данни, категориите '
           + 'субекти, инструкциите, поверителността, съдействието при упражняване на '
           + 'права и правилата за изтриване или връщане на данните.'}
        </p>
      </section>

      <section className="block">
        <SectionHead title="Сигурност" />
        <p>
          {'Прилагат се технически и организационни мерки за ограничаване на достъпа, '
           + 'защита на базата данни, контрол на административните права, журнал на '
           + 'промените, резервни копия и защита срещу неразрешено изменение или '
           + 'изтриване.'}
        </p>
        <p>
          {'Достъп до административния панел и базата данни имат само лица, за които '
           + 'това е необходимо. Когато е възможно, идентификатори се съхраняват в '
           + 'псевдонимизиран вид и не се публикуват национални идентификатори.'}
        </p>
      </section>

      <section className="block">
        <SectionHead title="Нарушения на сигурността" />
        <p>
          {'При установено нарушение на сигурността на личните данни се прави оценка '
           + 'на риска за засегнатите лица и се предприемат мерки за ограничаване на '
           + 'последиците. Когато е приложимо, се уведомяват компетентният надзорен '
           + 'орган и засегнатите лица съгласно приложимите правила.'}
        </p>
      </section>

      <p className="muted prose-note">
        {'Тази политика описва как сайтът обработва лични данни и как могат да бъдат '
         + 'упражнени права или подадени сигнали за грешки. Политиката може да бъде '
         + 'актуализирана при промяна в начина на работа на сайта, използваните '
         + 'източници, техническите доставчици или приложимите правила. Актуалната '
         + 'версия се публикува на тази страница.'}
      </p>
    </div>
  );
}
